import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Wallet, TrendingUp, TrendingDown, Phone, Building2, Clock,
  Euro, PhoneIncoming, PhoneOutgoing, CreditCard, AlertTriangle,
  Download, Calendar, Flame, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  voice_inbound: { label: 'Chiamate AI (entrata)', color: '#0ea5e9', icon: PhoneIncoming },
  voice_outbound: { label: 'Chiamate uscenti', color: '#f59e0b', icon: PhoneOutgoing },
  voice_call: { label: 'Chiamate AI', color: '#0ea5e9', icon: Phone },
  clinic_setup: { label: 'Setup clinica', color: '#8b5cf6', icon: Building2 },
  clinic_annual: { label: 'Abbonamento annuale', color: '#10b981', icon: CreditCard },
  clinic_annual_renewal: { label: 'Rinnovo annuale', color: '#10b981', icon: CreditCard },
};

const PIE_COLORS = ['#0ea5e9', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444', '#64748b'];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-medium text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{typeof p.value === 'number' ? `€${p.value.toFixed(2)}` : p.value}</strong></p>
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, alert, trend }) {
  return (
    <div className={`rounded-xl border p-4 sm:p-5 ${alert ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wider truncate">{label}</p>
          <p className={`text-xl sm:text-2xl font-bold mt-1 ${alert ? 'text-red-600' : 'text-slate-800'}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs font-medium ${trend >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend).toFixed(0)}% vs mese prec.
            </div>
          )}
        </div>
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
      </div>
    </div>
  );
}

export default function CostDashboard() {
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [summaryRes, txnRes] = await Promise.all([
        fetch(`${API}/api/billing/cost-summary?days=${days}`, { headers }),
        fetch(`${API}/api/billing/transactions`, { headers }),
      ]);
      if (summaryRes.ok) setData(await summaryRes.json());
      if (txnRes.ok) setTransactions(await txnRes.json());
    } catch (err) {
      toast.error('Errore caricamento dati');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [days]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/billing/transactions/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transazioni_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV esportato');
    } catch {
      toast.error('Errore esportazione');
    } finally { setExporting(false); }
  };

  const pieData = data ? Object.entries(data.by_type || {}).map(([key, val]) => ({
    name: TYPE_LABELS[key]?.label || key,
    value: val.amount,
    color: TYPE_LABELS[key]?.color || '#94a3b8',
  })).filter(d => d.value > 0) : [];

  const lowBalance = data && data.wallet_balance < 10;
  const mc = data?.monthly_comparison;
  const spendTrend = mc && mc.previous_month?.total > 0
    ? ((mc.current_month.total - mc.previous_month.total) / mc.previous_month.total) * 100
    : null;
  const burn = data?.burn_rate;

  const MESI_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
  const formatMonth = (label) => {
    if (!label) return '';
    const [y, m] = label.split('-');
    return `${MESI_IT[parseInt(m, 10) - 1]} ${y}`;
  };

  return (
    <Layout>
      <div className="p-4 sm:p-8 max-w-7xl" data-testid="cost-dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Euro className="w-6 h-6 text-emerald-500" /> Dashboard Costi
            </h1>
            <p className="text-sm text-slate-500 mt-1">Analisi dettagliata spese, consumo e proiezioni</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1" data-testid="cost-period-selector">
              {[7, 30, 90, 180].map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    days === d ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {d === 7 ? '7gg' : d === 30 ? '30gg' : d === 90 ? '90gg' : '6 mesi'}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCSV}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {exporting ? 'Esporto...' : 'Esporta CSV'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : data ? (
          <>
            {/* Low balance warning */}
            {lowBalance && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="low-balance-warning">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Saldo basso: €{data.wallet_balance.toFixed(2)}</p>
                  <p className="text-xs text-red-600">
                    {burn && burn.days_remaining < 999
                      ? `A questo ritmo il wallet si esaurira in ~${burn.days_remaining} giorni.`
                      : 'Ricarica il wallet per evitare interruzioni del servizio.'}
                  </p>
                </div>
              </div>
            )}

            {/* KPI Cards — 5 columns */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <StatCard icon={Wallet} label="Saldo Wallet" value={`€${data.wallet_balance.toFixed(2)}`}
                sub={burn && burn.days_remaining < 999 ? `~${burn.days_remaining}gg rimanenti` : undefined}
                color={lowBalance ? 'bg-red-500' : 'bg-emerald-500'} alert={lowBalance} />
              <StatCard icon={Euro} label={`Speso (${days}gg)`} value={`€${data.total_spent.toFixed(2)}`}
                sub={`${data.total_calls} transazioni`} color="bg-sky-500" trend={spendTrend} />
              <StatCard icon={Clock} label="Minuti Consumati" value={`${data.total_minutes}`}
                sub={`${data.total_calls} chiamate`} color="bg-amber-500" />
              <StatCard icon={Phone} label="Costo Medio/Chiamata"
                value={data.total_calls > 0 ? `€${(data.total_spent / data.total_calls).toFixed(2)}` : '€0'}
                sub={`In: €${data.pricing?.voice_per_minute_inbound || '?'}/min`}
                color="bg-violet-500" />
              <StatCard icon={Flame} label="Burn Rate"
                value={burn ? `€${burn.monthly.toFixed(0)}/mese` : '€0'}
                sub={burn ? `€${burn.daily.toFixed(2)}/giorno` : undefined}
                color="bg-orange-500" />
            </div>

            {/* Monthly Comparison */}
            {mc && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="monthly-current">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-sky-500" />
                    <h3 className="text-sm font-semibold text-slate-700">Mese Corrente ({formatMonth(mc.current_month.label)})</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Speso</p>
                      <p className="text-lg font-bold text-slate-800">€{mc.current_month.total.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Minuti</p>
                      <p className="text-lg font-bold text-slate-800">{mc.current_month.minutes}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Chiamate</p>
                      <p className="text-lg font-bold text-slate-800">{mc.current_month.calls}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="monthly-previous">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <h3 className="text-sm font-semibold text-slate-700">Mese Precedente ({formatMonth(mc.previous_month.label)})</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Speso</p>
                      <p className="text-lg font-bold text-slate-800">€{mc.previous_month.total.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Minuti</p>
                      <p className="text-lg font-bold text-slate-800">{mc.previous_month.minutes}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Chiamate</p>
                      <p className="text-lg font-bold text-slate-800">{mc.previous_month.calls}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Spending Trend */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="spending-trend">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Andamento Spesa Giornaliera</h3>
                {data.daily_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={data.daily_trend}>
                      <defs>
                        <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `€${v}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="amount" name="Spesa" stroke="#10b981" fill="url(#colorSpend)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-sm text-slate-400 py-12">Nessuna spesa nel periodo</p>
                )}
              </div>

              {/* Breakdown Pie */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="type-breakdown">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Ripartizione Costi</h3>
                {pieData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65}
                          paddingAngle={3} dataKey="value">
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color || PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 mt-2">
                      {pieData.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                            <span className="text-slate-600">{d.name}</span>
                          </div>
                          <span className="font-medium text-slate-800">€{d.value.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-center text-sm text-slate-400 py-12">Nessun dato</p>
                )}
              </div>
            </div>

            {/* Per-Clinic Detailed Table */}
            {data.by_clinic?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="clinic-breakdown">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Dettaglio Costi per Clinica
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-2 text-xs font-medium text-slate-500 uppercase">Clinica</th>
                        <th className="text-right py-2 text-xs font-medium text-sky-500 uppercase">Entrata</th>
                        <th className="text-right py-2 text-xs font-medium text-amber-500 uppercase">Uscita</th>
                        <th className="text-right py-2 text-xs font-medium text-slate-500 uppercase">Totale</th>
                        <th className="text-right py-2 text-xs font-medium text-slate-500 uppercase">Minuti</th>
                        <th className="text-right py-2 text-xs font-medium text-slate-500 uppercase">Chiamate</th>
                        <th className="text-right py-2 text-xs font-medium text-slate-500 uppercase">% Spesa</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {data.by_clinic.map((clinic, i) => {
                        const inbound = clinic.by_type?.voice_inbound || { amount: 0, minutes: 0, count: 0 };
                        const outbound = clinic.by_type?.voice_outbound || { amount: 0, minutes: 0, count: 0 };
                        const pct = data.total_spent > 0 ? (clinic.amount / data.total_spent * 100) : 0;
                        return (
                          <tr key={i} className="hover:bg-slate-50" data-testid={`clinic-cost-${i}`}>
                            <td className="py-2.5 font-medium text-slate-700">{clinic.clinic_name}</td>
                            <td className="py-2.5 text-right text-sky-600">€{inbound.amount.toFixed(2)}</td>
                            <td className="py-2.5 text-right text-amber-600">€{outbound.amount.toFixed(2)}</td>
                            <td className="py-2.5 text-right font-semibold text-slate-800">€{clinic.amount.toFixed(2)}</td>
                            <td className="py-2.5 text-right text-slate-600">{clinic.minutes}</td>
                            <td className="py-2.5 text-right text-slate-600">{clinic.count}</td>
                            <td className="py-2.5 text-right">
                              <div className="inline-flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-slate-500">{pct.toFixed(0)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="recent-transactions">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">Ultime Transazioni</h3>
                <span className="text-xs text-slate-400">{transactions.length} totali</span>
              </div>
              {transactions.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">Nessuna transazione</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {transactions.slice(0, 25).map((txn, i) => {
                    const typeInfo = TYPE_LABELS[txn.type] || { label: txn.type, color: '#94a3b8', icon: Euro };
                    const TxnIcon = typeInfo.icon;
                    return (
                      <div key={i} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`txn-${i}`}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${typeInfo.color}15` }}>
                          <TxnIcon className="w-4 h-4" style={{ color: typeInfo.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700">{txn.description}</p>
                          <p className="text-xs text-slate-400">{txn.created_at ? new Date(txn.created_at).toLocaleString('it-IT') : ''}</p>
                        </div>
                        {txn.minutes && (
                          <span className="text-xs text-slate-400 shrink-0">{txn.minutes} min</span>
                        )}
                        <p className="text-sm font-semibold text-slate-800 shrink-0">-€{txn.amount?.toFixed(2)}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
